import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight, Upload, Check, Camera, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ============================================================
   25-PAGE PROFILE SETUP WIZARD
   - One main question per page
   - Optional partner preference per page
   - Page X of 25 progress bar
   - Back arrow + Next button (disabled until valid)
   - Smooth slide animation between pages
   ============================================================ */

const TOTAL_PAGES = 25;

const HAIR_COLORS = ["Black", "Brown", "Blonde", "Red", "Other"];
const EYE_COLORS = ["Brown", "Black", "Blue", "Green", "Hazel", "Other"];
const EDUCATION_OPTIONS = ["High School", "College", "Graduate", "Postgraduate", "Other"];
const RELIGION_OPTIONS = [
  "Hindu", "Muslim", "Christian", "Sikh", "Buddhist", "Jewish",
  "Spiritual", "Atheist", "Agnostic", "Other", "Prefer not to say",
];
const LANGUAGE_OPTIONS = [
  "English", "Hindi", "Spanish", "French", "Mandarin", "Arabic", "Punjabi",
  "Bengali", "German", "Portuguese", "Japanese", "Korean", "Italian", "Russian", "Urdu",
];
const HOBBY_OPTIONS = [
  "Reading", "Travel", "Music", "Cooking", "Sports", "Yoga", "Movies", "Art",
  "Gaming", "Hiking", "Photography", "Dancing", "Fitness", "Writing", "Gardening",
];

/* ---------------- Tiny shared building blocks ---------------- */

function SectionLabel({ children }) {
  return (
    <p
      className="text-[#FF3B6B] text-[11px] uppercase tracking-[0.2em] font-semibold mb-2"
      style={{ fontFamily: "'JetBrains Mono', monospace" }}
    >
      {children}
    </p>
  );
}

function Card({ children, className = "" }) {
  return (
    <div className={`bg-white border border-[#F0E6DD] rounded-2xl p-5 shadow-sm ${className}`}>
      {children}
    </div>
  );
}

function OptionList({ options, value, onChange, name, multi = false, dense = false }) {
  const isSelected = (opt) =>
    multi ? Array.isArray(value) && value.includes(opt) : value === opt;

  const handle = (opt) => {
    if (multi) {
      const curr = Array.isArray(value) ? value : [];
      if (curr.includes(opt)) onChange(curr.filter((v) => v !== opt));
      else onChange([...curr, opt]);
    } else {
      onChange(opt);
    }
  };

  return (
    <div className={`grid ${dense ? "grid-cols-2" : "grid-cols-1"} gap-2`}>
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          data-testid={`opt-${name}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
          onClick={() => handle(opt)}
          className={`text-left px-4 py-3 rounded-xl border-2 transition-all text-sm ${
            isSelected(opt)
              ? "border-[#FF3B6B] bg-[#FFF1F5] text-[#1A1A1A] font-semibold"
              : "border-[#F0E6DD] bg-white text-[#555] hover:border-[#FFC2D0]"
          }`}
          style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
        >
          <span className="flex items-center justify-between gap-3">
            <span>{opt}</span>
            {isSelected(opt) && <Check className="w-4 h-4 text-[#FF3B6B] flex-shrink-0" />}
          </span>
        </button>
      ))}
    </div>
  );
}

function TextField({ value, onChange, placeholder, testid, type = "text" }) {
  return (
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full px-4 py-3 rounded-xl border-2 border-[#F0E6DD] bg-white text-[#1A1A1A] placeholder-[#B0A090] focus:border-[#FF3B6B] focus:outline-none transition-colors text-sm"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    />
  );
}

function ImageUpload({ value, onChange, testid, label = "Tap to upload photo" }) {
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(file);
  };
  return (
    <label
      className="flex flex-col items-center justify-center gap-2 w-full py-6 rounded-xl border-2 border-dashed border-[#FFC2D0] bg-[#FFF8FB] cursor-pointer hover:bg-[#FFF1F5] transition-colors"
      data-testid={testid}
    >
      <input type="file" accept="image/*" onChange={handleFile} className="hidden" />
      {value ? (
        <img src={value} alt="upload" className="w-32 h-32 object-cover rounded-lg" />
      ) : (
        <>
          <Camera className="w-7 h-7 text-[#FF3B6B]" />
          <span className="text-sm text-[#FF3B6B] font-medium">{label}</span>
        </>
      )}
    </label>
  );
}

/* ============================================================
   ALL 25 PAGES — each is a small render fn returning JSX
   answers = { ...all values }, set = (key, val) => ...
   pref    = { ...partner pref values }, setPref = (key, val) => ...
   isValid = boolean for enabling Next →
   ============================================================ */

function buildPages(answers, set, pref, setPref) {
  return [
    /* 1 — Name */
    {
      key: "name",
      title: "What is your first and last name?",
      hint: "Use the exact name shown on your birth certificate.",
      isValid: !!(answers.first_name?.trim() && answers.last_name?.trim()),
      render: () => (
        <>
          <Card>
            <SectionLabel>About you</SectionLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <TextField
                value={answers.first_name}
                onChange={(v) => set("first_name", v)}
                placeholder="First name"
                testid="q1-first-name"
              />
              <TextField
                value={answers.last_name}
                onChange={(v) => set("last_name", v)}
                placeholder="Last name"
                testid="q1-last-name"
              />
            </div>
          </Card>
          <PrefBlock
            label="Do you care about your partner's name?"
            options={["No preference", "Prefer traditional names", "Prefer modern names"]}
            value={pref.name}
            onChange={(v) => setPref("name", v)}
            name="pref-name"
          />
        </>
      ),
    },

    /* 2 — Birth Certificate */
    {
      key: "birth_certificate",
      title: "What is your original name as per your birth certificate?",
      hint: "Optional photo helps speed up verification.",
      isValid: !!answers.birth_certificate_name?.trim(),
      render: () => (
        <>
          <Card>
            <SectionLabel>Birth Certificate</SectionLabel>
            <TextField
              value={answers.birth_certificate_name}
              onChange={(v) => set("birth_certificate_name", v)}
              placeholder="Full name on birth certificate"
              testid="q2-bc-name"
            />
            <div className="mt-3">
              <ImageUpload
                value={answers.birth_certificate_photo}
                onChange={(v) => set("birth_certificate_photo", v)}
                testid="q2-bc-upload"
                label="Upload photo of birth certificate"
              />
            </div>
          </Card>
          <PrefBlock
            label="Do you prefer a partner with verified identity?"
            options={["Yes, very important", "Somewhat important", "Doesn't matter"]}
            value={pref.identity}
            onChange={(v) => setPref("identity", v)}
            name="pref-identity"
          />
        </>
      ),
    },

    /* 3 — Gender */
    {
      key: "gender",
      title: "Your gender?",
      isValid: !!answers.gender,
      render: () => (
        <>
          <Card>
            <SectionLabel>You</SectionLabel>
            <OptionList
              options={["Male", "Female"]}
              value={answers.gender}
              onChange={(v) => set("gender", v)}
              name="q3-gender"
              dense
            />
          </Card>
          <PrefBlock
            label="What gender do you prefer?"
            options={["Male", "Female"]}
            value={pref.gender}
            onChange={(v) => setPref("gender", v)}
            name="pref-gender"
            dense
          />
        </>
      ),
    },

    /* 4 — DOB */
    {
      key: "dob",
      title: "What is your date of birth?",
      hint: answers.dob ? `You are ${calcAge(answers.dob)} years old.` : "We use this to calculate your age.",
      isValid: !!answers.dob && calcAge(answers.dob) >= 18,
      render: () => (
        <>
          <Card>
            <SectionLabel>Date of Birth</SectionLabel>
            <TextField
              type="date"
              value={answers.dob}
              onChange={(v) => set("dob", v)}
              testid="q4-dob"
            />
            {answers.dob && calcAge(answers.dob) < 18 && (
              <p className="mt-2 text-xs text-[#E8335F]">You must be at least 18 years old.</p>
            )}
          </Card>
          <PrefBlock
            label="What age group do you prefer in a partner?"
            options={["18–21", "22–25", "26–30", "30+", "Doesn't matter"]}
            value={pref.age_group}
            onChange={(v) => setPref("age_group", v)}
            name="pref-age"
          />
        </>
      ),
    },

    /* 5 — Height */
    {
      key: "height",
      title: "What is your height?",
      isValid: !!answers.height?.trim(),
      render: () => (
        <>
          <Card>
            <SectionLabel>Height</SectionLabel>
            <TextField
              value={answers.height}
              onChange={(v) => set("height", v)}
              placeholder={`e.g., 5'8" / 173 cm`}
              testid="q5-height"
            />
          </Card>
          <PrefBlock
            label="What height do you prefer in a partner?"
            options={["Shorter than me", "Same height", "Taller than me", "Doesn't matter"]}
            value={pref.height}
            onChange={(v) => setPref("height", v)}
            name="pref-height"
          />
        </>
      ),
    },

    /* 6 — Hair Color */
    {
      key: "hair_color",
      title: "What is your hair color?",
      isValid: !!answers.hair_color,
      render: () => (
        <>
          <Card>
            <SectionLabel>Hair color</SectionLabel>
            <OptionList
              options={HAIR_COLORS}
              value={answers.hair_color}
              onChange={(v) => set("hair_color", v)}
              name="q6-hair"
              dense
            />
          </Card>
          <PrefBlock
            label="What hair color do you prefer in a partner?"
            options={[...HAIR_COLORS.filter((h) => h !== "Other"), "Doesn't matter"]}
            value={pref.hair_color}
            onChange={(v) => setPref("hair_color", v)}
            name="pref-hair"
            dense
          />
        </>
      ),
    },

    /* 7 — Eye Color */
    {
      key: "eye_color",
      title: "What is your eye color?",
      isValid: !!answers.eye_color,
      render: () => (
        <>
          <Card>
            <SectionLabel>Eye color</SectionLabel>
            <OptionList
              options={EYE_COLORS}
              value={answers.eye_color}
              onChange={(v) => set("eye_color", v)}
              name="q7-eye"
              dense
            />
          </Card>
          <PrefBlock
            label="What eye color do you prefer in a partner?"
            options={[...EYE_COLORS.filter((e) => e !== "Other"), "Doesn't matter"]}
            value={pref.eye_color}
            onChange={(v) => setPref("eye_color", v)}
            name="pref-eye"
            dense
          />
        </>
      ),
    },

    /* 8 — Education */
    {
      key: "education",
      title: "What is your education level?",
      isValid: !!answers.education,
      render: () => (
        <>
          <Card>
            <SectionLabel>Education</SectionLabel>
            <OptionList
              options={EDUCATION_OPTIONS}
              value={answers.education}
              onChange={(v) => set("education", v)}
              name="q8-education"
            />
          </Card>
          <PrefBlock
            label="What education level do you prefer in a partner?"
            options={["Highly educated", "Moderately educated", "Doesn't matter"]}
            value={pref.education}
            onChange={(v) => setPref("education", v)}
            name="pref-education"
          />
        </>
      ),
    },

    /* 9 — Occupation */
    {
      key: "occupation",
      title: "What do you do for work?",
      isValid: !!answers.occupation?.trim(),
      render: () => (
        <>
          <Card>
            <SectionLabel>Occupation</SectionLabel>
            <TextField
              value={answers.occupation}
              onChange={(v) => set("occupation", v)}
              placeholder="e.g., Software Engineer"
              testid="q9-occupation"
            />
          </Card>
          <PrefBlock
            label="Do you prefer a specific career type?"
            options={["Professional career", "Business / Entrepreneur", "Creative field", "Doesn't matter"]}
            value={pref.career}
            onChange={(v) => setPref("career", v)}
            name="pref-career"
          />
        </>
      ),
    },

    /* 10 — Location */
    {
      key: "location",
      title: "Where are you located?",
      isValid:
        !!(answers.city?.trim() && answers.state?.trim() && answers.country?.trim()),
      render: () => (
        <>
          <Card>
            <SectionLabel>Location</SectionLabel>
            <div className="grid grid-cols-1 gap-3">
              <TextField value={answers.city} onChange={(v) => set("city", v)} placeholder="City" testid="q10-city" />
              <TextField value={answers.state} onChange={(v) => set("state", v)} placeholder="State / Province" testid="q10-state" />
              <TextField value={answers.country} onChange={(v) => set("country", v)} placeholder="Country" testid="q10-country" />
            </div>
          </Card>
          <PrefBlock
            label="Preferred partner location?"
            options={["Same city", "Same country", "Open to long distance", "Doesn't matter"]}
            value={pref.location}
            onChange={(v) => setPref("location", v)}
            name="pref-location"
          />
        </>
      ),
    },

    /* 11 — Religion */
    {
      key: "religion",
      title: "What is your religion?",
      hint: "Optional — you can choose 'Prefer not to say'.",
      isValid: true, // optional
      render: () => (
        <>
          <Card>
            <SectionLabel>Religion</SectionLabel>
            <OptionList
              options={RELIGION_OPTIONS}
              value={answers.religion}
              onChange={(v) => set("religion", v)}
              name="q11-religion"
              dense
            />
          </Card>
          <PrefBlock
            label="Do you prefer same religion?"
            options={["Yes", "No", "Doesn't matter"]}
            value={pref.religion}
            onChange={(v) => setPref("religion", v)}
            name="pref-religion"
          />
        </>
      ),
    },

    /* 12 — Languages */
    {
      key: "languages",
      title: "What languages do you speak?",
      isValid: Array.isArray(answers.languages) && answers.languages.length > 0,
      render: () => (
        <>
          <Card>
            <SectionLabel>Languages</SectionLabel>
            <OptionList
              options={LANGUAGE_OPTIONS}
              value={answers.languages || []}
              onChange={(v) => set("languages", v)}
              name="q12-lang"
              multi
              dense
            />
          </Card>
          <PrefBlock
            label="Preferred language match?"
            options={["Same language", "Open to any", "Doesn't matter"]}
            value={pref.languages}
            onChange={(v) => setPref("languages", v)}
            name="pref-lang"
          />
        </>
      ),
    },

    /* 13 — Hobbies */
    {
      key: "hobbies",
      title: "What are your hobbies?",
      isValid: Array.isArray(answers.hobbies) && answers.hobbies.length > 0,
      render: () => (
        <>
          <Card>
            <SectionLabel>Hobbies</SectionLabel>
            <OptionList
              options={HOBBY_OPTIONS}
              value={answers.hobbies || []}
              onChange={(v) => set("hobbies", v)}
              name="q13-hobbies"
              multi
              dense
            />
          </Card>
          <PrefBlock
            label="Do shared hobbies matter?"
            options={["Very important", "Somewhat", "Not important"]}
            value={pref.hobbies}
            onChange={(v) => setPref("hobbies", v)}
            name="pref-hobbies"
          />
        </>
      ),
    },

    /* 14 — Personality */
    {
      key: "personality",
      title: "How would you describe your personality?",
      isValid: !!answers.personality,
      render: () => (
        <>
          <Card>
            <SectionLabel>Personality</SectionLabel>
            <OptionList
              options={["Introvert", "Extrovert", "Ambivert"]}
              value={answers.personality}
              onChange={(v) => set("personality", v)}
              name="q14-personality"
            />
          </Card>
          <PrefBlock
            label="Preferred personality type?"
            options={["Introvert", "Extrovert", "Doesn't matter"]}
            value={pref.personality}
            onChange={(v) => setPref("personality", v)}
            name="pref-personality"
          />
        </>
      ),
    },

    /* 15 — Relationship Intent */
    {
      key: "relationship_intent",
      title: "What are you looking for?",
      isValid: !!answers.relationship_intent,
      render: () => (
        <>
          <Card>
            <SectionLabel>Relationship Intent</SectionLabel>
            <OptionList
              options={["Serious relationship", "Casual", "Marriage", "Not sure"]}
              value={answers.relationship_intent}
              onChange={(v) => set("relationship_intent", v)}
              name="q15-intent"
            />
          </Card>
          <PrefBlock
            label="What should your partner want?"
            options={["Same as me", "Serious only", "Doesn't matter"]}
            value={pref.relationship_intent}
            onChange={(v) => setPref("relationship_intent", v)}
            name="pref-intent"
          />
        </>
      ),
    },

    /* 16 — Fitness */
    {
      key: "fitness",
      title: "How active are you?",
      isValid: !!answers.fitness,
      render: () => (
        <>
          <Card>
            <SectionLabel>Fitness</SectionLabel>
            <OptionList
              options={["Very active", "Moderate", "Not active"]}
              value={answers.fitness}
              onChange={(v) => set("fitness", v)}
              name="q16-fitness"
            />
          </Card>
          <PrefBlock
            label="Preferred activity level?"
            options={["Active", "Any", "Doesn't matter"]}
            value={pref.fitness}
            onChange={(v) => setPref("fitness", v)}
            name="pref-fitness"
          />
        </>
      ),
    },

    /* 17 — Hygiene */
    {
      key: "hygiene",
      title: "What is your hygiene & cleanliness style?",
      isValid: !!answers.hygiene,
      render: () => (
        <>
          <Card>
            <SectionLabel>Hygiene & Cleanliness</SectionLabel>
            <OptionList
              options={[
                "Very Clean — highly organized and neat",
                "Balanced — sometimes clean, sometimes lived-in",
                "Weekend Warrior — clean on weekends",
                "Time-Dependent — depends on free time",
                "Relaxed — not a top priority",
              ]}
              value={answers.hygiene}
              onChange={(v) => set("hygiene", v)}
              name="q17-hygiene"
            />
          </Card>
          <PrefBlock
            label="What do you prefer in a partner?"
            options={[
              "Must be very clean and tidy",
              "Balanced middle-ground",
              "Doesn't matter — I'm flexible",
            ]}
            value={pref.hygiene}
            onChange={(v) => setPref("hygiene", v)}
            name="pref-hygiene"
          />
        </>
      ),
    },

    /* 18 — Diet */
    {
      key: "diet",
      title: "What is your diet?",
      isValid: !!answers.diet,
      render: () => (
        <>
          <Card>
            <SectionLabel>Diet</SectionLabel>
            <OptionList
              options={["Vegetarian", "Non-vegetarian", "Vegan"]}
              value={answers.diet}
              onChange={(v) => set("diet", v)}
              name="q18-diet"
            />
          </Card>
          <PrefBlock
            label="Preferred partner diet?"
            options={["Same as me", "Doesn't matter"]}
            value={pref.diet}
            onChange={(v) => setPref("diet", v)}
            name="pref-diet"
          />
        </>
      ),
    },

    /* 19 — Alcohol */
    {
      key: "alcohol",
      title: "Do you consume alcohol?",
      isValid: !!answers.alcohol,
      render: () => (
        <>
          <Card>
            <SectionLabel>Alcohol</SectionLabel>
            <OptionList
              options={[
                "Never",
                "Once in a blue moon",
                "Occasionally",
                "Weekends only",
                "Regularly (every day)",
                "Struggling with it",
              ]}
              value={answers.alcohol}
              onChange={(v) => set("alcohol", v)}
              name="q19-alcohol"
            />
          </Card>
          <PrefBlock
            label="What kind of partner do you prefer?"
            options={[
              "Never drinks",
              "Once in a blue moon",
              "Occasionally",
              "Weekends only",
              "Regular drinker",
              "Doesn't matter",
            ]}
            value={pref.alcohol}
            onChange={(v) => setPref("alcohol", v)}
            name="pref-alcohol"
          />
        </>
      ),
    },

    /* 20 — Smoke */
    {
      key: "smoke",
      title: "Do you smoke?",
      isValid: !!answers.smoke,
      render: () => (
        <>
          <Card>
            <SectionLabel>Smoking</SectionLabel>
            <OptionList
              options={[
                "Never",
                "Once in a blue moon",
                "Occasionally",
                "Weekends only",
                "Regular every day",
                "Struggling with it",
              ]}
              value={answers.smoke}
              onChange={(v) => set("smoke", v)}
              name="q20-smoke"
            />
          </Card>
          <PrefBlock
            label="What kind of partner do you prefer?"
            options={[
              "Never smokes",
              "Once in a blue moon",
              "Occasionally",
              "Weekends only",
              "Regular smoker",
              "Doesn't matter",
            ]}
            value={pref.smoke}
            onChange={(v) => setPref("smoke", v)}
            name="pref-smoke"
          />
        </>
      ),
    },

    /* 21 — Drugs */
    {
      key: "drugs",
      title: "Do you use drugs?",
      isValid: !!answers.drugs,
      render: () => (
        <>
          <Card>
            <SectionLabel>Drugs</SectionLabel>
            <OptionList
              options={[
                "Never",
                "Once in a blue moon",
                "Occasionally",
                "Weekends only",
                "Regular every day",
                "Struggling with it",
              ]}
              value={answers.drugs}
              onChange={(v) => set("drugs", v)}
              name="q21-drugs"
            />
          </Card>
          <PrefBlock
            label="What kind of partner do you prefer?"
            options={[
              "Never uses",
              "Once in a blue moon",
              "Occasionally",
              "Weekends only",
              "Regular user",
              "Doesn't matter",
            ]}
            value={pref.drugs}
            onChange={(v) => setPref("drugs", v)}
            name="pref-drugs"
          />
        </>
      ),
    },

    /* 22 — Daily Chores */
    {
      key: "daily_chores",
      title: "Do you handle your own daily chores?",
      hint: "Cooking, cleaning, laundry, dishes, home maintenance.",
      isValid: !!answers.daily_chores,
      render: () => (
        <>
          <Card>
            <SectionLabel>Household Chores</SectionLabel>
            <OptionList
              options={[
                "Proactive — I do it all myself",
                "Minimalist — I avoid them if I can",
                "Periodic — I handle them on a schedule",
                "Outsourced — I hire help",
                "Flexible — when I have free time",
                "Balanced — sometimes yes, sometimes no",
              ]}
              value={answers.daily_chores}
              onChange={(v) => set("daily_chores", v)}
              name="q22-chores"
            />
          </Card>
          <PrefBlock
            label="What arrangement do you prefer with a partner?"
            options={[
              "Self-Reliant — I do everything",
              "Partner-Led — partner handles chores",
              "Equal Split — 50/50 share",
              "Tag-Team — we take turns",
              "Hired Help — outside help",
              "Supported — partner hires help",
              "Relaxed — we don't worry about it",
            ]}
            value={pref.daily_chores}
            onChange={(v) => setPref("daily_chores", v)}
            name="pref-chores"
          />
        </>
      ),
    },

    /* 23 — Kids / Family */
    {
      key: "kids",
      title: "Children & family plans",
      isValid: !!(answers.has_kids && answers.wants_kids),
      render: () => (
        <>
          <Card>
            <SectionLabel>Do you have kids?</SectionLabel>
            <OptionList
              options={["Yes", "No"]}
              value={answers.has_kids}
              onChange={(v) => set("has_kids", v)}
              name="q23-has-kids"
              dense
            />
          </Card>
          <Card className="mt-3">
            <SectionLabel>Do you want kids in the future?</SectionLabel>
            <OptionList
              options={["Yes", "No", "Not sure / Undecided", "Already have enough"]}
              value={answers.wants_kids}
              onChange={(v) => set("wants_kids", v)}
              name="q23-wants-kids"
            />
          </Card>
          <PrefBlock
            label="What kind of partner do you prefer regarding children?"
            options={[
              "Someone who already has kids",
              "Someone who does not have kids",
              "Someone who wants kids in the future",
              "Someone who does not want kids",
              "No preference",
            ]}
            value={pref.kids}
            onChange={(v) => setPref("kids", v)}
            name="pref-kids"
          />
        </>
      ),
    },

    /* 24 — Living Arrangement */
    {
      key: "living_arrangement",
      title: "What is your current living situation?",
      isValid: !!answers.living_arrangement,
      render: () => (
        <>
          <Card>
            <SectionLabel>Current living situation</SectionLabel>
            <OptionList
              options={[
                "Independent",
                "PG / Shared Housing",
                "With Parents / Family",
                "With Friends / Siblings",
                "Work-related Accommodation",
              ]}
              value={answers.living_arrangement}
              onChange={(v) => set("living_arrangement", v)}
              name="q24-living"
            />
          </Card>
          <PrefBlock
            label="My ideal future partnership"
            options={[
              "Traditional — one stays home, one works",
              "The 50/50 Split — shared duties & careers",
              "Fluid / Adaptive — take turns based on life stages",
              "Equal partners in everything",
            ]}
            value={pref.partnership}
            onChange={(v) => setPref("partnership", v)}
            name="pref-partnership"
          />
        </>
      ),
    },

    /* 25 — Photo Upload */
    {
      key: "photos",
      title: "Upload your photos",
      hint: "1 full-body + 1 portrait. Refresh every 30 days. No AI / heavy filters.",
      isValid: !!(answers.full_body_photo && answers.portrait_photo),
      render: () => (
        <>
          <Card>
            <SectionLabel>Required photos</SectionLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-[#8A8077] mb-2 font-semibold">Full-body photo (head-to-toe)</p>
                <ImageUpload
                  value={answers.full_body_photo}
                  onChange={(v) => set("full_body_photo", v)}
                  testid="q25-full-body"
                  label="Upload full-body"
                />
              </div>
              <div>
                <p className="text-xs text-[#8A8077] mb-2 font-semibold">Portrait / headshot (shoulders up)</p>
                <ImageUpload
                  value={answers.portrait_photo}
                  onChange={(v) => set("portrait_photo", v)}
                  testid="q25-portrait"
                  label="Upload portrait"
                />
              </div>
            </div>
            <div className="mt-4 p-3 rounded-lg bg-[#FFF1F5] border border-[#FFD0DC]">
              <p className="text-[11px] text-[#8A6E4A] leading-relaxed">
                <strong>Photo standards:</strong> updated every 30 days · 100% authentic · no AI, no heavy filters, no fake backgrounds · natural lighting only.
              </p>
            </div>
          </Card>
          <PrefBlock
            label="What do you prefer in a partner's photos?"
            options={[
              "Must follow the 30-day refresh rule",
              "Just authentic photos (any age is fine)",
              "Doesn't matter",
            ]}
            value={pref.photos}
            onChange={(v) => setPref("photos", v)}
            name="pref-photos"
          />
        </>
      ),
    },
  ];
}

/* Partner preference block (reused on every page that has one) */
function PrefBlock({ label, options, value, onChange, name, dense = false }) {
  return (
    <Card className="mt-3 bg-[#FFF8FB] border-[#FFD0DC]">
      <SectionLabel>Partner Preference</SectionLabel>
      <p
        className="text-[#1A1A1A] text-sm font-medium mb-3"
        style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
      >
        {label}
      </p>
      <OptionList options={options} value={value} onChange={onChange} name={name} dense={dense} />
    </Card>
  );
}

function calcAge(dobStr) {
  if (!dobStr) return 0;
  const dob = new Date(dobStr);
  if (isNaN(dob)) return 0;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age;
}

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
export default function ProfileSetup({ user, token, onComplete }) {
  const [pageIdx, setPageIdx] = useState(0);
  const [direction, setDirection] = useState("forward"); // for slide animation
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [answers, setAnswers] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    languages: [],
    hobbies: [],
  });
  const [partnerPrefs, setPartnerPrefs] = useState({});

  const setAns = (k, v) => setAnswers((s) => ({ ...s, [k]: v }));
  const setPref = (k, v) => setPartnerPrefs((s) => ({ ...s, [k]: v }));

  const pages = useMemo(
    () => buildPages(answers, setAns, partnerPrefs, setPref),
    [answers, partnerPrefs]
  );

  const current = pages[pageIdx];
  const isLast = pageIdx === TOTAL_PAGES - 1;
  const progress = ((pageIdx + 1) / TOTAL_PAGES) * 100;

  const goNext = () => {
    if (!current.isValid) return;
    if (isLast) {
      submit();
      return;
    }
    setDirection("forward");
    setPageIdx((i) => i + 1);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    if (pageIdx === 0) return;
    setDirection("back");
    setPageIdx((i) => i - 1);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* swipe support */
  const swipeRef = useState({ x: 0 })[0];
  const onTouchStart = (e) => {
    swipeRef.x = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - swipeRef.x;
    if (dx < -60) goNext();
    else if (dx > 60) goBack();
  };

  const submit = async () => {
    setError("");
    setSubmitting(true);
    try {
      const fullName = `${answers.first_name} ${answers.last_name}`.trim();
      const locStr = [answers.city, answers.state, answers.country].filter(Boolean).join(", ");
      const photos = [answers.portrait_photo, answers.full_body_photo].filter(Boolean);
      const payload = {
        age: calcAge(answers.dob) || undefined,
        gender: answers.gender,
        bio: `${fullName} · ${answers.occupation || ""}`.trim(),
        photos,
        location: locStr,
        education: answers.education,
        occupation: answers.occupation,
        hobbies: Array.isArray(answers.hobbies) ? answers.hobbies.join(", ") : answers.hobbies,
        looking_for: answers.relationship_intent,
        interests: Array.isArray(answers.hobbies) ? answers.hobbies : [],
        birth_certificate_name: answers.birth_certificate_name,
        birth_certificate_photo: answers.birth_certificate_photo,
        dob: answers.dob,
        height: answers.height,
        hair_color: answers.hair_color,
        eye_color: answers.eye_color,
        religion: answers.religion,
        languages: answers.languages,
        personality: answers.personality,
        fitness: answers.fitness,
        hygiene: answers.hygiene,
        diet: answers.diet,
        alcohol: answers.alcohol,
        smoke: answers.smoke,
        drugs: answers.drugs,
        daily_chores: answers.daily_chores,
        has_kids: answers.has_kids,
        wants_kids: answers.wants_kids,
        living_arrangement: answers.living_arrangement,
        relationship_intent: answers.relationship_intent,
        answers,
        partner_preferences: partnerPrefs,
        profile_setup_25_complete: true,
      };
      const res = await fetch(`${API}/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to save profile");
      onComplete(data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#FFF9F5] overflow-y-auto"
      data-testid="profile-setup-25"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Sticky top bar */}
      <header className="sticky top-0 z-30 bg-[#FFF9F5]/95 backdrop-blur-md border-b border-[#F0E6DD]">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            data-testid="profile-back-btn"
            onClick={goBack}
            disabled={pageIdx === 0}
            className={`p-2 rounded-full transition-colors ${
              pageIdx === 0
                ? "text-[#D8CBBF] cursor-not-allowed"
                : "text-[#1A1A1A] hover:bg-[#F5EBE2]"
            }`}
            aria-label="Back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1.5">
              <span
                className="text-[11px] uppercase tracking-[0.2em] text-[#8A8077] font-semibold"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                Page {pageIdx + 1} of {TOTAL_PAGES}
              </span>
              <span
                className="text-[11px] text-[#FF3B6B] font-semibold"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                {Math.round(progress)}%
              </span>
            </div>
            <div className="h-1.5 w-full bg-[#F0E6DD] rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#FF3B6B] to-[#FF8A3D] transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-2xl mx-auto px-4 py-6 pb-32">
        <div
          key={current.key}
          className={`animate-slide-${direction}`}
          style={{
            animation: `${direction === "forward" ? "slideInRight" : "slideInLeft"} 0.35s ease-out`,
          }}
        >
          <h1
            style={{ fontFamily: "'Bodoni Moda', serif" }}
            className="text-3xl sm:text-4xl tracking-tight text-[#1A1A1A] leading-snug mb-2"
            data-testid="profile-page-title"
          >
            {current.title}
          </h1>
          {current.hint && (
            <p
              className="text-[#8A6E4A] text-sm mb-5"
              style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
            >
              {current.hint}
            </p>
          )}
          <div className="space-y-2">{current.render()}</div>
        </div>

        {error && (
          <div className="mt-4 p-3 rounded-lg bg-[#FFE8EE] border border-[#FFC2D0] text-[#E8335F] text-sm">
            {error}
          </div>
        )}
      </main>

      {/* Sticky bottom Next button */}
      <footer className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-[#FFF9F5] via-[#FFF9F5]/95 to-transparent pt-4 pb-4 px-4 z-20">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
          <span
            className="text-xs text-[#8A8077]"
            style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
          >
            {current.isValid ? "Looks good — tap Next" : "Please fill the required field"}
          </span>
          <button
            type="button"
            data-testid="profile-next-btn"
            onClick={goNext}
            disabled={!current.isValid || submitting}
            className={`inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold shadow-lg transition-all ${
              current.isValid && !submitting
                ? "bg-[#FF3B6B] text-white hover:bg-[#E8335F] shadow-[#FF3B6B]/30"
                : "bg-[#E8DACD] text-[#9A8C7E] cursor-not-allowed shadow-none"
            }`}
            style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Saving…
              </>
            ) : isLast ? (
              <>
                Finish & Get Verified <Check className="w-4 h-4" />
              </>
            ) : (
              <>
                Next <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </footer>

      {/* Slide animation keyframes (scoped to this component via global rule) */}
      <style>{`
        @keyframes slideInRight {
          0% { opacity: 0; transform: translateX(24px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideInLeft {
          0% { opacity: 0; transform: translateX(-24px); }
          100% { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
